import { useEffect, useState } from "react";

export type ThemePref = "light" | "dark" | "system";

const KEY = "theme";

// Stored preference. "system" (the default) is represented by no stored value,
// matching the no-flash script in index.html.
export function getStoredTheme(): ThemePref {
  const v = typeof localStorage !== "undefined" ? localStorage.getItem(KEY) : null;
  return v === "light" || v === "dark" ? v : "system";
}

function prefersDark() {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

// Toggle the `dark` class on <html> for the given preference.
export function applyThemeClass(pref: ThemePref) {
  const dark = pref === "dark" || (pref === "system" && prefersDark());
  document.documentElement.classList.toggle("dark", dark);
}

// Read + set the theme preference; re-applies the class and, while on "system",
// follows OS changes.
export function useTheme(): [ThemePref, (p: ThemePref) => void] {
  const [pref, setPref] = useState<ThemePref>(getStoredTheme);

  useEffect(() => {
    if (pref !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyThemeClass("system");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [pref]);

  const set = (p: ThemePref) => {
    if (p === "system") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, p);
    setPref(p);
    applyThemeClass(p);
  };

  return [pref, set];
}
